'use client'

import Link from 'next/link'
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import languageApi from '~/apis/language'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { IconLeft } from 'react-day-picker'

const normalizeKeyword = (value: string) => value.trim().toLowerCase()

export default function VocabularyLanguagesClient() {
    const [query, setQuery] = useState('')

    const languagesQuery = useQuery({
        queryKey: ['languages', 'active'],
        queryFn: () => languageApi.getActiveLanguages()
    })

    const filteredLanguages = useMemo(() => {
        const keyword = normalizeKeyword(query)
        const languages = languagesQuery.data ?? []

        if (!keyword) {
            return languages
        }

        return languages.filter((language) => {
            const haystacks = [language.name, language.nativeName, language.code, language.slug]
            return haystacks.some((item) => item.trim().toLowerCase().includes(keyword))
        })
    }, [languagesQuery.data, query])

    return (
        <main className='mx-auto w-full max-w-(--breakpoint-xl) px-4 py-6 sm:px-6 sm:py-8'>
            <div className='mb-6'>
                <div className='mb-3'>
                    <Button asChild variant='outline' size='sm'>
                        <Link href='/languages'>Tools</Link>
                    </Button>
                </div>
                <h1 className='text-foreground text-2xl font-semibold sm:text-3xl'>Chọn ngôn ngữ để học từ vựng</h1>
                <p className='text-muted-foreground mt-2 text-sm'>
                    Bắt đầu từ ngôn ngữ, tiếp theo chọn bài học, rồi vào màn hình luyện tập riêng cho bài đó.
                </p>
            </div>

            <section className='rounded-xl border border-border bg-card p-4'>
                <div className='grid gap-3 md:grid-cols-[1fr_auto]'>
                    <Input
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder='Tìm theo tên, mã hoặc slug ngôn ngữ'
                    />
                    <Badge variant='secondary' className='justify-center px-3 py-2'>
                        {filteredLanguages.length} ngôn ngữ
                    </Badge>
                </div>
            </section>

            <section className='mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3'>
                {languagesQuery.isPending &&
                    Array.from({ length: 6 }).map((_, index) => (
                        <article
                            key={`language-skeleton-${index}`}
                            className='rounded-xl border border-border bg-card p-5 shadow-sm'
                        >
                            <div className='h-5 w-40 animate-pulse rounded bg-muted' />
                            <div className='mt-3 h-4 w-24 animate-pulse rounded bg-muted' />
                            <div className='mt-6 h-9 w-36 animate-pulse rounded bg-muted' />
                        </article>
                    ))}

                {!languagesQuery.isPending &&
                    filteredLanguages.map((language) => (
                        <article
                            key={language.id}
                            className='flex h-full flex-col rounded-xl border border-border bg-card p-5 shadow-sm'
                        >
                            <div className='flex flex-wrap items-center gap-2'>
                                <h2 className='text-lg font-semibold text-foreground'>{language.nativeName || language.name}</h2>
                                <Badge variant='outline'>{language.code}</Badge>
                            </div>
                            <p className='mt-2 text-sm text-muted-foreground'>{language.name}</p>
                            <p className='mt-1 text-xs uppercase tracking-[0.12em] text-muted-foreground'>
                                slug: {language.slug}
                            </p>
                            <div className='mt-5'>
                                <Button asChild>
                                    <Link href={`/languages/vocabulary/${language.id}`}>Xem danh sách bài học</Link>
                                </Button>
                            </div>
                        </article>
                    ))}
            </section>

            {!languagesQuery.isPending && !filteredLanguages.length && (
                <section className='mt-4 rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground'>
                    Không tìm thấy ngôn ngữ phù hợp.
                </section>
            )}
        </main>
    )
}
